import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import { AdminLayout } from '@/components/layouts/admin-layout';
import FilterBar from '@/components/common/filter-bar';
import { VirtualizedGrid } from '@/components/common/virtualized-grid';
import { ModerationItemCard } from '@/components/admin/moderation-item-card';
import { MODERATION_FILTERS } from '@/lib/shared-types/filters';
import {
  MessageCircle,
  Users,
  CheckCircle,
  XCircle,
  Eye,
  Flag,
  AlertCircle,
  VolumeX,
  History
} from 'lucide-react';

// Moderation item types
export interface ModerationItem {
  id: number;
  type: 'comment' | 'user' | 'stream';
  content: string;
  author: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  };
  stream?: {
    id: number;
    title: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'flagged';
  createdAt: string;
  reason?: string;
  priority: 'low' | 'medium' | 'high';
  reportCount?: number;
}

// Moderation statistics
interface ModerationStats {
  pendingComments: number;
  moderatedToday: number;
  flaggedContent: number;
  activeRestrictions: number;
  approvedToday: number;
  rejectedToday: number;
  totalModerated: number;
}

// API Response types
interface ModerationQueueResponse {
  items: ModerationItem[];
  total: number;
  page: number;
  pageSize: number;
}

interface ModerationStatsResponse {
  stats: {
    totalPending: number;
    totalModerated: number;
    byType: {
      comments: number;
      users: number;
      streams: number;
    };
    recentActivity: ModerationItem[];
  };
  queue: ModerationItem[];
}

interface ActionResponse {
  message: string;
}

// User restriction types
interface UserRestriction {
  id: number;
  userId: number;
  user: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  };
  type: 'mute' | 'ban_from_chat' | 'ban_from_stream';
  reason: string;
  expiresAt?: string;
  createdAt: string;
  createdBy: string;
}

export default function AdminModerationPage() {
  const { isInitialized } = useAuthStore();

  // State
  const [queue, setQueue] = useState<ModerationItem[]>([]);
  const [stats, setStats] = useState<ModerationStats | null>(null);
  const [userRestrictions, setUserRestrictions] = useState<UserRestriction[]>([]);
  const [comments, setComments] = useState<ModerationItem[]>([]);
  const [flaggedUsers, setFlaggedUsers] = useState<ModerationItem[]>([]);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restrictionsLoading, setRestrictionsLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(true);

   // Filter state managed by FilterBar
   const [filters, setFilters] = useState<Record<string, string | string[]>>({});

  // Dialog states
  const [selectedItem, setSelectedItem] = useState<ModerationItem | null>(null);
  const [moderationDialog, setModerationDialog] = useState(false);
  const [moderationAction, setModerationAction] = useState<string>('');
  const [moderationReason, setModerationReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

   // Pagination
   const [page, setPage] = useState(1);
   const [hasMore, setHasMore] = useState(false);
   const [total, setTotal] = useState(0);
   const [totalCount, setTotalCount] = useState(0);
   const pageSize = 20;

   // Fetch moderation queue
   const fetchModerationQueue = useCallback(async () => {
     try {
       setLoading(true);
       setError(null);

       const params = new URLSearchParams({
         page: page.toString(),
         pageSize: pageSize.toString(),
         ...filters
       });

       const response = await api.get<ModerationQueueResponse>(`/api/moderator/moderation-queue?${params}`);

       if (response.success && response.data) {
         const data = response.data as unknown as ModerationQueueResponse;
         setQueue(prev => {
           if (page === 1) {
             return data.items || [];
           }
           return [...prev, ...(data.items || [])];
         });
         const fetchedCount = (data.items || []).length;
         const serverTotal = data.total || 0;
         setTotal(serverTotal);
         setTotalCount(serverTotal);
         setHasMore(fetchedCount === pageSize && page * pageSize < serverTotal);
       } else {
         setError(response.error?.message || 'Failed to load moderation queue');
       }
     } catch (err) {
       console.error('Error fetching moderation queue:', err);
       setError('Failed to load moderation queue');
     } finally {
       setLoading(false);
     }
   }, [page, filters]);

   const loadMore = () => {
     if (hasMore && !loading) {
       setPage(p => p + 1);
     }
   };

  // Fetch moderation stats
  const fetchModerationStats = useCallback(async () => {
    try {
      const response = await api.get<ModerationStatsResponse>('/api/moderator/moderation-queue?limit=100');

      if (response.success && response.data) {
        const data = response.data as unknown as ModerationStatsResponse;
        const statsData = data.stats;
        const queueData = data.queue || [];
        setStats({
          pendingComments: statsData.byType.comments || 0,
          moderatedToday: statsData.totalModerated || 0,
          flaggedContent: queueData.filter(item => item.status === 'flagged').length || 0,
          activeRestrictions: 0,
          approvedToday: queueData.filter(item => item.status === 'approved').length || 0,
          rejectedToday: queueData.filter(item => item.status === 'rejected').length || 0,
          totalModerated: statsData.totalModerated || 0
        });
      }
    } catch (err) {
      console.error('Error fetching moderation stats:', err);
      toast.error('Failed to load moderation stats');
    }
  }, []);

  // Fetch comments for moderation
  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const response = await api.get<ModerationQueueResponse>('/api/moderator/comments?moderated=false&limit=50');

      if (response.success && response.data) {
        const data = response.data as unknown as ModerationQueueResponse;
        setComments(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      toast.error('Failed to load comments for moderation');
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  // Fetch user restrictions
  const fetchUserRestrictions = useCallback(async () => {
    try {
      setRestrictionsLoading(true);
      const response = await api.get<{ restrictions: UserRestriction[] }>('/api/admin/moderation/restrictions');
      if (response.success && response.data?.restrictions) {
        setUserRestrictions(response.data.restrictions);
      } else {
        setUserRestrictions([]);
      }
    } catch (err) {
      console.error('Error fetching user restrictions:', err);
      setUserRestrictions([]);
    } finally {
      setRestrictionsLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (isInitialized) {
      fetchModerationQueue();
      fetchModerationStats();
      fetchComments();
      fetchUserRestrictions();
    }
  }, [isInitialized, fetchModerationQueue, fetchModerationStats, fetchComments, fetchUserRestrictions]);

  // Handle moderation action
  const handleModerate = async () => {
    if (!selectedItem) return;

    setIsProcessing(true);
    try {
      const actionMap: Record<string, string> = {
        'approve': 'approve',
        'hide': 'hide',
        'delete': 'delete',
        'reject': 'delete',
        'flag': 'flag'
      };

      const backendAction = actionMap[moderationAction] || moderationAction;

      const response = await api.put<ActionResponse>(`/api/moderator/comments/${selectedItem.id}/moderate`, {
        action: backendAction,
        reason: moderationReason
      });

      if (response.success) {
        // Remove from queue and refresh
        setQueue(prev => prev.filter(i => i.id !== selectedItem.id));
        setComments(prev => prev.filter(i => i.id !== selectedItem.id));
        fetchModerationStats();
        const ACTION_PAST: Record<string, string> = { approve: 'approved', hide: 'hidden', delete: 'deleted' };
        toast.success(`Content ${ACTION_PAST[moderationAction] ?? moderationAction + 'd'} successfully`);
      } else {
        toast.error(response.error?.message || 'Failed to process moderation action');
      }
    } catch (err) {
      console.error('Error moderating item:', err);
      toast.error('Failed to process moderation action');
      setError('Failed to process moderation action');
    } finally {
      setIsProcessing(false);
      setModerationDialog(false);
      setSelectedItem(null);
      setModerationReason('');
    }
  };

  // Handle user restriction
  const handleRestrictUser = async (userId: number, action: string, reason: string, duration?: number) => {
    try {
      const response = await api.post<ActionResponse>(`/api/moderator/users/${userId}/restrict`, {
        action,
        reason,
        duration
      });

      if (response.success) {
        fetchUserRestrictions();
        fetchModerationStats();
        toast.success('User restriction applied');
      } else {
        toast.error(response.error?.message || 'Failed to restrict user');
      }
    } catch (err) {
      console.error('Error restricting user:', err);
      toast.error('Failed to restrict user');
      setError('Failed to restrict user');
    }
  };

   // Filter queue items
   const filteredQueue = queue.filter(item => {
     const search = (filters.search as string) || '';
     const status = (filters.status as string) || '';
     const dateRange = (filters.dateRange as string) || 'all';

     const matchesSearch = search === '' ||
       item.content.toLowerCase().includes(search.toLowerCase()) ||
       item.author.name.toLowerCase().includes(search.toLowerCase());
     const matchesStatus = status === '' || status === 'all' || item.status === status;
     const matchesDate = dateRange === 'all' || checkDateRange(item.createdAt, dateRange);
     return matchesSearch && matchesStatus && matchesDate;
   });

  // Check date range helper
  const checkDateRange = (dateString: string, range: string): boolean => {
    const date = new Date(dateString);
    const now = new Date();

    switch (range) {
      case 'today':
        return date.toDateString() === now.toDateString();
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return date >= weekAgo;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return date >= monthAgo;
      default:
        return true;
    }
  };

  // Helper functions
  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      'low': 'secondary',
      'medium': 'default',
      'high': 'destructive'
    };
    return <Badge variant={variants[priority] || 'secondary'} className="capitalize">{priority}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      'pending': 'secondary',
      'approved': 'default',
      'rejected': 'destructive',
      'flagged': 'destructive',
      'hidden': 'outline'
    };
    const labels: Record<string, string> = {
      'pending': 'Pending',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'flagged': 'Flagged',
      'hidden': 'Hidden'
    };
    return <Badge variant={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'comment': return <MessageCircle className="h-4 w-4" />;
      case 'user': return <Users className="h-4 w-4" />;
      case 'stream': return <Eye className="h-4 w-4" />;
      default: return <Flag className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'comment': return 'text-blue-600 bg-blue-100';
      case 'user': return 'text-purple-600 bg-purple-100';
      case 'stream': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading state
  if (loading && queue.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout>
      {/* Main Content Tabs */}
      <Tabs defaultValue="queue" className="space-y-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="queue">Queue ({totalCount})</TabsTrigger>
          <TabsTrigger value="comments">Comments ({comments.length})</TabsTrigger>
          <TabsTrigger value="users">User Reports</TabsTrigger>
          <TabsTrigger value="restrictions">Restrictions ({stats?.activeRestrictions || 0})</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

         {/* Moderation Queue Tab */}
         <TabsContent value="queue" className="space-y-6">
         {/* Filters */}
           <div className="p-2 sm:p-3 @[740px]/admin-layout:p-4">
             <FilterBar
               filters={MODERATION_FILTERS}
               activeFilters={filters}
               onFilterChange={setFilters}
             />
           </div>

          {/* Queue Items */}
          {error ? (
            <Card>
              <CardContent className="p-12 text-center">
                <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Error Loading Queue</h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button variant="outline" onClick={fetchModerationQueue}>
                  Try Again
                </Button>
              </CardContent>
            </Card>
          ) : (
            <VirtualizedGrid
              items={filteredQueue}
              renderItem={(item) => (
                <ModerationItemCard
                  item={item}
                  onApprove={() => {
                    setSelectedItem(item);
                    setModerationAction('approve');
                    setModerationDialog(true);
                  }}
                  onHide={() => {
                    setSelectedItem(item);
                    setModerationAction('hide');
                    setModerationDialog(true);
                  }}
                  onDelete={() => {
                    setSelectedItem(item);
                    setModerationAction('delete');
                    setModerationDialog(true);
                  }}
                  onMute={() => handleRestrictUser(item.author.id, 'mute', 'Inappropriate behavior')}
                  showMuteButton={item.type === 'user'}
                  isProcessing={isProcessing}
                  processingAction={moderationAction}
                />
              )}
              keyExtractor={(item) => item.id.toString()}
              columns={{ sm: 1, md: 1, lg: 2, xl: 2 }}
              gap="gap-4"
              loading={loading && queue.length === 0}
              hasNextPage={hasMore}
              onLoadNextPage={loadMore}
              loadMoreThreshold={5}
              itemHeight={200}
              emptyMessage={
                Object.keys(filters).length > 0
                  ? 'No items match your current filters'
                  : 'All content has been moderated. Great job!'
              }
            />
          )}
        </TabsContent>

        {/* Comments Tab */}
        <TabsContent value="comments" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Comments Moderation</CardTitle>
              <CardDescription>
                Review and moderate user comments across all streams.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {commentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No comments pending</h3>
                  <p className="text-muted-foreground">
                    All comments have been reviewed or no comments exist.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.slice(0, 20).map((comment) => (
                    <div key={comment.id} className="flex items-start gap-4 p-4 border rounded-lg">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={comment.author.avatar} />
                        <AvatarFallback>
                          {comment.author.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{comment.author.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(comment.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                        {comment.stream && (
                          <p className="text-xs text-muted-foreground">
                            on: {comment.stream.title}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedItem(comment);
                            setModerationAction('approve');
                            setModerationDialog(true);
                          }}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedItem(comment);
                            setModerationAction('delete');
                            setModerationDialog(true);
                          }}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>User Reports</CardTitle>
              <CardDescription>
                Review user behavior reports and take appropriate action.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">User Reports</h3>
                <p className="text-muted-foreground">
                  User behavior reports and violations will appear here.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Restrictions Tab */}
        <TabsContent value="restrictions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Active Restrictions</CardTitle>
              <CardDescription>
                View and manage active user restrictions (mutes, bans).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {restrictionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : userRestrictions.length === 0 ? (
                <div className="text-center py-12">
                  <VolumeX className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No active restrictions</h3>
                  <p className="text-muted-foreground">
                    No users are currently restricted from commenting.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userRestrictions.map((restriction) => (
                    <div key={restriction.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={restriction.user.avatar} />
                          <AvatarFallback>
                            {restriction.user.name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{restriction.user.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {restriction.type === 'mute' ? 'Muted' : restriction.type === 'ban_from_chat' ? 'Chat Banned' : 'Stream Banned'}
                            {restriction.expiresAt && ` until ${formatDate(restriction.expiresAt)}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await api.delete(`/api/moderator/users/${restriction.userId}/restriction`,
                                { body: JSON.stringify({ restrictionType: restriction.type }) });
                              setUserRestrictions(prev => prev.filter(r => r.id !== restriction.id));
                              toast.success('Restriction removed');
                            } catch {
                              toast.error('Failed to remove restriction');
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Moderation History</CardTitle>
              <CardDescription>
                View past moderation actions and decisions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Moderation History</h3>
                <p className="text-muted-foreground">
                  View past moderation actions, appeals, and moderator decisions.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Moderation Dialog */}
      <Dialog open={moderationDialog} onOpenChange={setModerationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Moderate Content</DialogTitle>
            <DialogDescription>
              {selectedItem && `Review and moderate this ${selectedItem.type}`}
            </DialogDescription>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-4">
              {/* Content Preview */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={selectedItem.author.avatar} />
                    <AvatarFallback>
                      {selectedItem.author.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{selectedItem.author.name}</span>
                </div>
                <p className="text-sm">{selectedItem.content}</p>
              </div>

              {/* Action Info */}
              <div className="text-sm">
                <p className="font-medium">Action: <span className="capitalize">{moderationAction}</span></p>
              </div>

              {/* Reason Input */}
              <div>
                <Label htmlFor="reason">Reason (optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Provide a reason for this moderation action..."
                  value={moderationReason}
                  onChange={(e) => setModerationReason(e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Dialog Actions */}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setModerationDialog(false)} disabled={isProcessing}>
                  Cancel
                </Button>
                <Button onClick={handleModerate} disabled={isProcessing}>
                  {isProcessing ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Processing...
                    </>
                  ) : (
                    `Confirm ${moderationAction.charAt(0).toUpperCase() + moderationAction.slice(1)}`
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <p className="text-destructive">{error}</p>
              </div>
              <Button variant="outline" onClick={fetchModerationQueue} disabled={loading}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
